import moment from 'moment'

import type { IPostData, IAccountData, CsvDownloadCommon } from './csv-download-common.service'

import { app } from '../../app.module'

interface IYtPostData extends IPostData {
  ytPostId: string
  title: string
  views: number
  likes: number
  dislikes: number
  comments: number
  duration: number
  hashtagName: string
  hiddenSubscribersFlg: number
}

interface IYtAccountdata extends IAccountData {
  views
  number
  likes: number
  dislikes: number
  comments: number
  durations: number
  hiddenSubscribersFlg: number
}

/**
 * CsvダウンロードYoutubeサービス
 */
export class CsvDownloadYoutube {
  constructor(
    private CsvDownloadCommon: CsvDownloadCommon,
    private $filter: any
  ) {}

  /**
   * 投稿CSVデータをセット
   * @param {IYtPostData[]} postDatas Yt投稿データ
   * @returns {string[][]} CSVデータ
   */
  public setPostCsv(postDatas: IYtPostData[]): string[][] {
    // CSVデータ
    const csvData = []
    // ヘッダー
    const header = [
      'No',
      '投稿日付',
      'アカウント名',
      '大カテゴリ',
      '小カテゴリ',
      'フォロワー数',
      '投稿タイトル',
      '投稿内容',
      '視聴回数',
      '高く評価',
      '低く評価',
      'コメント',
      '動画秒数',
      '動画タグ',
      '投稿タグ',
      '画像URL',
      '投稿先URL',
      'アカウントID',
      '投稿ID',
    ]

    // ヘッダーを格納
    csvData.push(header)
    // データを格納
    postDatas.forEach((postData, index) => {
      // タグリスト
      let tagLists = ''

      // グローバルタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.globalTagNames)
      // グループタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.groupTagNames)
      // ユーザータグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.userTagNames)

      if (postData.hiddenSubscribersFlg === 1) {
        postData.postDateFollowers = '非公開'
      }

      csvData.push([
        index + 1,
        moment(postData.publishDate).format('YYYY/MM/DD HH:mm'),
        postData.accountScreenName,
        postData.accountCategoryName,
        postData.accountSubCategoryName,
        postData.postDateFollowers,
        postData.title,
        this.CsvDownloadCommon.convertPostMessage(postData.description),
        postData.views,
        postData.likes,
        postData.dislikes,
        postData.comments,
        postData.duration,
        postData.hashtagName,
        tagLists,
        postData.thumbnailUrl,
        'https://www.youtube.com/watch?v=' + postData.ytPostId,
        postData.accountId,
        postData.ytPostId,
      ])
    })
    return csvData
  }

  /**
   * アカウントCSVデータをセット
   * @param {IYtAccountdata[]} accountDatas アカウントデータ
   * @param {boolean} dataPerPost 1投稿あたりフラグ
   * @returns {string[][]} CSVデータ
   */
  public setAccountCsv(accountDatas: IYtAccountdata[], dataPerPost: boolean): string[][] {
    // CSVデータ
    const csvData = []
    // ヘッダー
    let header = []

    if (dataPerPost === true) {
      header = [
        'No',
        'アカウント名',
        'ブランド名',
        '大カテゴリ',
        '小カテゴリ',
        'フォロワー数',
        'フォロワー増減率(%)',
        '視聴回数(1投稿あたり)',
        '高く評価(1投稿あたり)',
        '低く評価(1投稿あたり)',
        'コメント(1投稿あたり)',
        '動画秒数(1投稿あたり)',
        '動画数',
        'アカウントタグ',
      ]
    } else {
      header = [
        'No',
        'アカウント名',
        'ブランド名',
        '大カテゴリ',
        '小カテゴリ',
        'フォロワー数',
        'フォロワー増減率(%)',
        '視聴回数',
        '高く評価',
        '低く評価',
        'コメント',
        '動画秒数',
        '動画数',
        'アカウントタグ',
      ]
    }

    // ヘッダーを格納
    csvData.push(header)

    // データを格納
    accountDatas.forEach((account, index) => {
      const engageData = [
        account.views,
        account.likes,
        account.dislikes,
        account.comments,
        account.durations,
      ]
      const ratioData = [account.posts]

      // 1投稿ごとの反応数が選択されている場合は、反応数を投稿数ごとに割る
      if (dataPerPost === true) {
        engageData.forEach((element, index) => {
          engageData[index] = this.$filter('setNumberFormat')(
            element,
            1,
            'number',
            true,
            account.posts
          )
        })
      }
      // タグリスト
      let tagLists = ''

      // グローバルタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.globalTagNames)
      // グループタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.groupTagNames)
      // ユーザータグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.userTagNames)

      if (account.hiddenSubscribersFlg === 1) {
        account.followers = '非公開'
      }

      // フォロワー増減率
      let followersUpdownRate = '-'
      if (account.followersUpdownRate !== null) {
        followersUpdownRate = String(account.followersUpdownRate)
      }

      let accountData = [
        index + 1,
        account.screenName,
        account.brandName,
        account.categoryName,
        account.subCategoryName,
        account.followers,
        followersUpdownRate,
      ]

      accountData = accountData.concat(engageData, ratioData, [tagLists])

      csvData.push(accountData)
    })

    return csvData
  }

  /**
   * アカウント詳細のグラフCSVをセット
   * @param  {string} graphType グラフタイプ
   * @param  {boolean} dataPerObjectFlg 1投稿ごとフラグ(1アカウントごとフラグ)
   * @param {array} periodArray 期間リスト
   * @param  {object} data データ
   * @returns {string[][]} CSVデータ
   */
  public setAccountChangeCsv(
    graphType: string,
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): string[][] {
    switch (graphType) {
      case 'post_count':
        return this.CsvDownloadCommon.setAccountChangePostCount(dataPerObjectFlg, periodArray, data)
      case 'account_views':
        return this.setAccountChangeViewsCsv(dataPerObjectFlg, periodArray, data)
      case 'account_likes':
        return this.setAccountChangeLikesCsv(dataPerObjectFlg, periodArray, data)
      case 'account_dislikes':
        return this.setAccountChangeDisLikesCsv(dataPerObjectFlg, periodArray, data)
      case 'account_comments':
        return this.setAccountChangeDisLikesCsv(dataPerObjectFlg, periodArray, data)
    }
  }

  /**
   * アカウントの投稿の対象CSVデータをセット
   * @param  {string} analyticsTab 対象名
   * @param  {string} snsType SNSタイプ
   * @param  {boolean} dataPerPostFlg 1投稿ごとフラグ
   * @param  {array} datas データ
   * @return {string[][]} CSVデータ
   */
  public setAccountTargetCsv(
    targetName: string,
    dataPerPostFlg: boolean,
    datas: any[]
  ): string[][] {
    const csvData = []

    if (targetName === 'ハッシュタグ') {
      targetName = '動画タグ'
    }

    const dataPerPostText = dataPerPostFlg === true ? '(1投稿あたり)' : ''
    const header = [
      targetName,
      '投稿数' + dataPerPostText,
      '視聴回数' + dataPerPostText,
      '高く評価' + dataPerPostText,
      '低く評価' + dataPerPostText,
      'コメント' + dataPerPostText,
    ]

    if (targetName !== '動画秒数') {
      header.push('動画秒数' + dataPerPostText)
    }

    csvData.push(header)

    // 1投稿ごとではない場合
    if (dataPerPostFlg === false) {
      datas.forEach((data) => {
        const csvRow = [
          data.targetIndex,
          data.posts,
          data.views,
          data.likes,
          data.dislikes,
          data.comments,
        ]
        if (targetName !== '動画秒数') {
          csvRow.push(data.durations)
        }
        csvData.push(csvRow)
      })
    } else {
      datas.forEach((data) => {
        const csvRow = [
          data.targetIndex,
          data.posts,
          data.viewsPerPost,
          data.likesPerPost,
          data.dislikesPerPost,
          data.commentsPerPost,
        ]
        if (targetName !== '動画秒数') {
          csvRow.push(data.durationsPerPost)
        }

        csvData.push(csvRow)
      })
    }

    return csvData
  }

  /**
   * アカウント詳細グラフの視聴回数CSVをセット
   * @param dataPerObjectFlg 一投稿あたりフラグ
   * @param periodArray 期間リスト
   * @param data データ
   * @returns {string[][]} CSVデータ
   */
  private setAccountChangeViewsCsv(dataPerObjectFlg: boolean, periodArray: {}[], data): string[][] {
    // CSVデータ
    const csvData = []

    if (dataPerObjectFlg === false) {
      csvData.push(['期間', '視聴回数'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.data[key]])
      }
    } else {
      csvData.push(['期間', '視聴回数(1投稿あたり)'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.dataPerPost[key]])
      }
    }

    return csvData
  }

  /**
   * アカウント詳細グラフの高く評価CSVをセット
   * @param dataPerObjectFlg 一投稿あたりフラグ
   * @param periodArray 期間リスト
   * @param data データ
   * @returns {string[][]} CSVデータ
   */
  private setAccountChangeLikesCsv(dataPerObjectFlg: boolean, periodArray: {}[], data): string[][] {
    // CSVデータ
    const csvData = []

    if (dataPerObjectFlg === false) {
      csvData.push(['期間', '高く評価'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.data[key]])
      }
    } else {
      csvData.push(['期間', '高く評価(1投稿あたり)'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.dataPerPost[key]])
      }
    }

    return csvData
  }

  /**
   * アカウント詳細グラフの低く評価CSVをセット
   * @param dataPerObjectFlg 一投稿あたりフラグ
   * @param periodArray 期間リスト
   * @param data データ
   * @returns {string[][]} CSVデータ
   */
  private setAccountChangeDisLikesCsv(
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): string[][] {
    // CSVデータ
    const csvData = []

    if (dataPerObjectFlg === false) {
      csvData.push(['期間', '低く評価'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.data[key]])
      }
    } else {
      csvData.push(['期間', '低く評価(1投稿あたり)'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.dataPerPost[key]])
      }
    }

    return csvData
  }

  /**
   * アカウント詳細グラフのコメントCSVをセット
   * @param dataPerObjectFlg 一投稿あたりフラグ
   * @param periodArray 期間リスト
   * @param data データ
   * @returns {string[][]} CSVデータ
   */
  private setAccountChangeCommentsCsv(
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): string[][] {
    // CSVデータ
    const csvData = []

    if (dataPerObjectFlg === false) {
      csvData.push(['期間', 'コメント'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.data[key]])
      }
    } else {
      csvData.push(['期間', 'コメント(1投稿あたり)'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.dataPerPost[key]])
      }
    }

    return csvData
  }
}

// CSVダウンロード用のサービスを定義
app.service('CsvDownloadYoutube', CsvDownloadYoutube)
